import { useLocation } from "wouter";
import { Logo, LogoMark } from "@/components/Logo";
import QuoteCarousel from "@/components/QuoteCarousel";
import { safeSetItem } from "@/lib/safeStorage";
import LanguagePill from "@/components/LanguagePill";
import { useTranslation } from "react-i18next";
import { ChevronRight, Heart, Shield, Zap, DollarSign, BookOpen, Infinity, Menu, X, Users, Building2, Feather } from "lucide-react";
import { useState, useEffect } from "react";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";
const CLAY = "#E8DAB2";

const pillBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "13px 28px",
  borderRadius: "50px",
  fontSize: "14px",
  fontWeight: 300,
  letterSpacing: "0.04em",
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "1px solid transparent",
  fontFamily: "inherit",
};

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    function onScroll() {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? scrolled / max : 0);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}

export default function About() {
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const progress = useScrollProgress();
  const [mobileOpen, setMobileOpen] = useState(false);

  function navigateAsDonor() {
    safeSetItem("pendingRole", "donor");
    navigate("/sign-up");
  }
  function navigateAsDebtor() {
    safeSetItem("pendingRole", "debtor");
    navigate("/sign-up");
  }

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>

      {/* Reading-progress bar */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "2px",
          width: `${progress * 100}%`,
          background: `linear-gradient(90deg, ${SAGE}, ${TERRACOTTA})`,
          zIndex: 60,
          transition: "width 0.1s linear",
          pointerEvents: "none",
        }}
      />

      {/* Navbar */}
      <nav style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        background: "rgba(252,252,252,0.88)",
        backdropFilter: "blur(16px)",
      }}>
        <div style={{ maxWidth: "1152px", margin: "0 auto", padding: "0 20px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={() => navigate("/")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            aria-label={t("nav.backHomeAriaLabel")}
          >
            <Logo iconSize={52} />
          </button>

          {/* Desktop right controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LanguagePill />
            <button
              style={{ ...pillBtn, background: "#0a0a0a", color: "#ffffff", border: "1px solid #0a0a0a", padding: "8px 18px", fontSize: "13px" }}
              onClick={() => navigate("/")}
              aria-label={t("nav.backToHome")}
            >
              {t("nav.backToHome")}
            </button>
            {/* Hamburger — shown only on small screens */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              style={{
                display: "none",
                background: "none",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "8px",
                padding: "6px 8px",
                cursor: "pointer",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="about-hamburger"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileOpen && (
          <div style={{
            borderTop: "1px solid rgba(0,0,0,0.06)",
            background: "rgba(252,252,252,0.97)",
            padding: "16px 20px 20px",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <button
                onClick={() => { navigate("/"); setMobileOpen(false); }}
                style={{ background: "none", border: "none", textAlign: "left", padding: "10px 12px", fontSize: "15px", fontWeight: 300, cursor: "pointer", borderRadius: "8px", color: "hsl(var(--foreground))" }}
              >
                ← {t("nav.backToHome").replace("← ", "")}
              </button>
              <hr style={{ border: "none", borderTop: "1px solid rgba(0,0,0,0.06)", margin: "8px 0" }} />
              <button
                onClick={() => { navigateAsDonor(); setMobileOpen(false); }}
                style={{ ...pillBtn, background: "#0a0a0a", color: "#fff", justifyContent: "center", width: "100%", padding: "12px 20px", borderRadius: "10px" }}
              >
                {t("about.donateNow")} <ChevronRight style={{ width: "16px", height: "16px" }} />
              </button>
              <button
                onClick={() => { navigateAsDebtor(); setMobileOpen(false); }}
                style={{ ...pillBtn, background: "transparent", color: "hsl(var(--foreground))", border: "1px solid rgba(0,0,0,0.15)", justifyContent: "center", width: "100%", padding: "12px 20px", borderRadius: "10px" }}
              >
                {t("about.applyForRelief")}
              </button>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        @media (max-width: 640px) {
          .about-hamburger { display: inline-flex !important; }
        }
      `}</style>

      {/* ── Hero ── */}
      <section style={{
        position: "relative",
        overflow: "hidden",
        background: "#0a0a0a",
        padding: "100px 20px 90px",
        textAlign: "center",
      }}>
        {/* Futuristic/abstract — Earth from orbit, dissolved into dark bg */}
        <img
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=80&fit=crop&auto=format"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.16, filter: "grayscale(0.15)", pointerEvents: "none" }}
        />
        <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, #0a0a0a 0%, transparent 22%, transparent 78%, #0a0a0a 100%)", pointerEvents: "none" }} />
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 70% 55% at 50% 40%, rgba(168,181,162,0.08) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 40% 40% at 80% 70%, rgba(217,184,163,0.06) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "700px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "40px" }}>
            <div style={{
              padding: "6px",
              borderRadius: "22px",
              border: "1px solid rgba(168,181,162,0.25)",
              background: "rgba(168,181,162,0.06)",
            }}>
              <LogoMark size={96} />
            </div>
          </div>

          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 16px",
            borderRadius: "50px",
            background: "rgba(168,181,162,0.10)",
            border: "1px solid rgba(168,181,162,0.22)",
            color: SAGE,
            fontSize: "11px",
            fontWeight: 300,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: "28px",
          }}>
            <Zap style={{ width: "10px", height: "10px" }} />
            {t("about.ourStory")}
          </div>

          <h1 style={{
            fontSize: "clamp(36px, 6vw, 64px)",
            fontWeight: 200,
            color: "#ffffff",
            lineHeight: 1.08,
            letterSpacing: "-0.04em",
            marginBottom: "24px",
          }}>
            {t("about.heroHeadline1")}<br />
            <span style={{ color: SAGE }}>{t("about.heroHeadline2")}</span>
          </h1>

          <p style={{
            fontSize: "17px",
            fontWeight: 300,
            color: "rgba(255,255,255,0.52)",
            lineHeight: 1.75,
            maxWidth: "540px",
            margin: "0 auto",
            letterSpacing: "0.01em",
          }}>
            {t("about.heroSubtext")}
          </p>
        </div>
      </section>

      {/* ── Founder ── */}
      <section style={{ position: "relative", padding: "96px 20px", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, rgba(217,184,163,0.08) 0%, rgba(232,218,178,0.04) 100%)`,
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "900px", margin: "0 auto" }}>
          <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: "10px" }}>
            {t("about.founderEyebrow")}
          </p>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "56px" }}>
            Joshua Julian
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "48px",
            alignItems: "start",
          }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${SAGE}44 0%, ${TERRACOTTA}44 100%)`,
                border: `2px solid ${SAGE}44`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "36px",
                fontWeight: 200,
                color: SAGE,
                letterSpacing: "-0.04em",
                flexShrink: 0,
              }}>
                JL
              </div>
              <div style={{ marginTop: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "13px", fontWeight: 400, color: "hsl(var(--foreground))", marginBottom: "2px" }}>Joshua Julian</div>
                <div style={{ fontSize: "11px", fontWeight: 300, color: "hsl(var(--muted-foreground))", letterSpacing: "0.03em" }}>{t("about.founderRole")}</div>
              </div>
            </div>

            <div>
              <p style={{ fontSize: "16px", fontWeight: 300, color: "hsl(var(--foreground))", lineHeight: 1.8, marginBottom: "20px", letterSpacing: "0.01em" }}>
                {t("about.founderBio1")}
              </p>
              <p style={{ fontSize: "15px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.8, marginBottom: "20px" }}>
                {t("about.founderBio2")}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
                {[
                  { labelKey: "about.belief1Label", textKey: "about.belief1Text", icon: Heart, color: TERRACOTTA },
                  { labelKey: "about.belief2Label", textKey: "about.belief2Text", icon: BookOpen, color: SAGE },
                  { labelKey: "about.belief3Label", textKey: "about.belief3Text", icon: Infinity, color: CLAY },
                ].map(({ labelKey, textKey, icon: Icon, color }) => (
                  <div
                    key={labelKey}
                    style={{
                      display: "flex",
                      gap: "16px",
                      padding: "20px 24px",
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(0,0,0,0.05)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <div style={{ padding: "8px", borderRadius: "9px", background: `${color}22`, border: `1px solid ${color}44`, flexShrink: 0, height: "fit-content" }}>
                      <Icon style={{ width: "16px", height: "16px", color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 500, color: "hsl(var(--foreground))", marginBottom: "6px", letterSpacing: "-0.01em" }}>{t(labelKey)}</div>
                      <p style={{ fontSize: "13px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.7, margin: 0 }}>{t(textKey)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: "14px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.8, fontStyle: "italic" }}>
                {t("about.founderQuote")}
              </p>
              <p style={{ fontSize: "12px", fontWeight: 400, color: SAGE, marginTop: "8px", letterSpacing: "0.02em" }}>{t("about.founderQuoteAttr")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pure Giving ── */}
      <section style={{ position: "relative", padding: "96px 20px", overflow: "hidden", background: "#0a0a0a" }}>
        {/* Charitable/community — volunteers, dissolved into dark bg */}
        <img
          src="https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.14, filter: "grayscale(0.2)", pointerEvents: "none" }}
        />
        <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, #0a0a0a 0%, transparent 25%, transparent 75%, #0a0a0a 100%)", pointerEvents: "none" }} />
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 60% 55% at 30% 50%, rgba(168,181,162,0.07) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 50% 45% at 80% 60%, rgba(217,184,163,0.05) 0%, transparent 55%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "900px", margin: "0 auto" }}>
          <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: SAGE, marginBottom: "10px" }}>
            {t("about.modelEyebrow")}
          </p>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 200, color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "16px" }}>
            {t("about.modelHeadline")}
          </h2>
          <p style={{ fontSize: "16px", fontWeight: 300, color: "rgba(255,255,255,0.50)", lineHeight: 1.75, maxWidth: "600px", marginBottom: "56px" }}>
            {t("about.modelSubtext")}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
            {[
              { step: "1", icon: DollarSign, titleKey: "about.processStep1Title", descKey: "about.processStep1Desc", color: SAGE },
              { step: "2", icon: Shield, titleKey: "about.processStep2Title", descKey: "about.processStep2Desc", color: TERRACOTTA },
              { step: "3", icon: Zap, titleKey: "about.processStep3Title", descKey: "about.processStep3Desc", color: CLAY },
              { step: "4", icon: Heart, titleKey: "about.processStep4Title", descKey: "about.processStep4Desc", color: SAGE },
            ].map(({ step, icon: Icon, titleKey, descKey, color }, i, arr) => (
              <div key={step} style={{ display: "flex", gap: "20px", alignItems: "stretch" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <div style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: `${color}22`,
                    border: `1.5px solid ${color}66`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Icon style={{ width: "15px", height: "15px", color }} />
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ width: "1.5px", flex: 1, background: "rgba(255,255,255,0.08)", margin: "6px 0" }} />
                  )}
                </div>
                <div style={{ paddingBottom: i < arr.length - 1 ? "32px" : "0", paddingTop: "6px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 400, color, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>{step}</div>
                  <div style={{ fontSize: "15px", fontWeight: 400, color: "#ffffff", marginBottom: "6px", letterSpacing: "-0.01em" }}>{t(titleKey)}</div>
                  <p style={{ fontSize: "13px", fontWeight: 300, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, margin: 0 }}>{t(descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: "56px",
            padding: "32px 36px",
            borderRadius: "20px",
            background: "rgba(168,181,162,0.08)",
            border: "1px solid rgba(168,181,162,0.18)",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 400, color: SAGE, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
              {t("impact.headline")}
            </div>
            <p style={{ fontSize: "15px", fontWeight: 300, color: "rgba(255,255,255,0.65)", lineHeight: 1.75, margin: "0 0 12px" }}>
              {t("impact.body")}
            </p>
            <p style={{ fontSize: "11px", fontWeight: 300, color: "rgba(255,255,255,0.28)", letterSpacing: "0.04em" }}>
              {t("impact.note")}
            </p>
          </div>
        </div>
      </section>

      {/* ── Cents to Freedom ── */}
      <section style={{ position: "relative", padding: "96px 20px", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, rgba(232,218,178,0.06) 0%, rgba(168,181,162,0.04) 100%)`,
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "900px", margin: "0 auto" }}>
          <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: CLAY, marginBottom: "10px" }}>
            {t("about.impactEyebrow")}
          </p>
          <h2 style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "16px" }}>
            {t("about.impactHeadline")}
          </h2>
          <p style={{ fontSize: "16px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.75, maxWidth: "600px", marginBottom: "56px" }}>
            {t("about.impactSubtext")}
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "2px",
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.06)",
            marginBottom: "48px",
          }}>
            {[
              { Icon: DollarSign, labelKey: "about.impactRow1", color: SAGE },
              { Icon: Users,      labelKey: "about.impactRow2", color: TERRACOTTA },
              { Icon: Building2,  labelKey: "about.impactRow3", color: CLAY },
              { Icon: Feather,    labelKey: "about.impactRow4", color: SAGE },
            ].map(({ Icon, labelKey, color }) => (
              <div
                key={labelKey}
                style={{
                  padding: "32px 20px",
                  background: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(8px)",
                  textAlign: "center",
                }}
              >
                <div style={{ marginBottom: "12px", display: "flex", justifyContent: "center" }}><Icon size={28} color={color} /></div>
                <div style={{ fontSize: "11px", fontWeight: 400, color, letterSpacing: "0.1em", textTransform: "uppercase" }}>{t(labelKey)}</div>
              </div>
            ))}
          </div>

          <div style={{
            padding: "36px 40px",
            borderRadius: "20px",
            background: "rgba(168,181,162,0.08)",
            border: "1px solid rgba(168,181,162,0.18)",
            borderLeft: `4px solid ${SAGE}`,
          }}>
            <p style={{ fontSize: "clamp(16px, 2.5vw, 20px)", fontWeight: 200, color: "hsl(var(--foreground))", lineHeight: 1.75, margin: 0, letterSpacing: "0.01em" }}>
              {t("about.impactQuote")}
            </p>
            <p style={{ fontSize: "12px", fontWeight: 400, color: SAGE, marginTop: "16px", marginBottom: 0, letterSpacing: "0.04em" }}>
              {t("about.impactQuoteAttr")}
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        position: "relative",
        padding: "96px 20px",
        overflow: "hidden",
        background: "#0a0a0a",
        textAlign: "center",
      }}>
        <div aria-hidden style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 60% 65% at 50% 50%, rgba(168,181,162,0.08) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "560px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 200, color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "16px" }}>
            {t("about.ctaHeadline")}
          </h2>
          <p style={{ color: "rgba(255,255,255,0.42)", fontWeight: 300, fontSize: "15px", marginBottom: "40px", lineHeight: 1.75 }}>
            {t("about.ctaSubtext")}
          </p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
            <button
              style={{ ...pillBtn, background: "#ffffff", color: "#0a0a0a" }}
              onClick={navigateAsDonor}
              aria-label={t("about.donateNow")}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.4)`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
            >
              {t("about.donateNow")} <ChevronRight style={{ width: "16px", height: "16px" }} />
            </button>
            <button
              style={{ ...pillBtn, background: "transparent", color: "rgba(255,255,255,0.65)", border: "1px solid rgba(255,255,255,0.2)" }}
              onClick={navigateAsDebtor}
              aria-label={t("about.applyForRelief")}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.07)";
                (e.currentTarget as HTMLButtonElement).style.borderColor = `rgba(217,184,163,0.4)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.2)";
              }}
            >
              {t("about.applyForRelief")}
            </button>
          </div>
        </div>
      </section>

      <QuoteCarousel />

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: "40px 20px" }}>
        <div style={{ maxWidth: "1152px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px" }}>
            <Logo iconSize={20} />
          </div>
          <p style={{ fontSize: "12px", fontWeight: 300, color: "hsl(var(--muted-foreground))", letterSpacing: "0.01em" }}>
            {t("footer.copyright")}
          </p>
        </div>
      </footer>
    </div>
  );
}
